import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { actualizarBultos } from '../../lib/entregasApi'

// QRCode lib se carga vía CDN (qrcodejs - mismo que el generador de QR portero)
function loadQRCodeLib() {
  return new Promise((resolve, reject) => {
    if (window.QRCode) return resolve(window.QRCode)
    const s = document.createElement('script')
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js'
    s.onload = () => resolve(window.QRCode)
    s.onerror = reject
    document.head.appendChild(s)
  })
}

const TIPO_LABEL = {
  suscripcion: 'SUSCRIPCIÓN',
  compra_unica: 'COMPRA ÚNICA',
  productos_sueltos: 'TIENDA',
  adicional_suscripcion: 'EXTRA SUSC.',
}

const VIVIENDA_LABEL = {
  casa: 'CASA',
  edificio: 'EDIFICIO',
  oficina: 'OFICINA',
}

export default function Etiquetas() {
  const [params] = useSearchParams()
  const ids = (params.get('ids') || '').split(',').filter(Boolean)
  const [entregas, setEntregas] = useState([])
  const [loading, setLoading] = useState(true)
  const [qrReady, setQrReady] = useState(false)
  const qrRefs = useRef({})

  useEffect(() => {
    loadQRCodeLib().then(() => setQrReady(true))

    if (ids.length === 0) { setLoading(false); return }
    ;(async () => {
      const { data, error } = await supabase
        .from('entregas')
        .select(`
          id, numero_orden, tracking_token, fecha_programada, ventana_horaria,
          tipo, productos_extras, notas, bultos_total,
          cliente:clientes!entregas_cliente_id_fkey(
            barrio, direccion, apto, dia_entrega, tipo_vivienda, alias,
            titular:profiles!clientes_titular_fkey(nombre),
            beneficiario:profiles!clientes_beneficiario_fkey(nombre),
            portero:porteros!clientes_portero_referido_id_fkey(
              lugar,
              profile:profiles!porteros_id_fkey(nombre)
            )
          ),
          kit:kits!entregas_kit_codigo_fkey(nombre, tier)
        `)
        .in('id', ids)
      if (error) console.error(error)
      // Para entregas SIN cliente suscriptor → mergear datos del pedido one-shot
      const entregasArr = data || []
      const sinCliente = entregasArr.filter(e => !e.cliente).map(e => e.id)
      if (sinCliente.length > 0) {
        const { data: peds } = await supabase
          .from('pedidos')
          .select(`
            entrega_id, comprador_nombre, comprador_telefono, entrega_direccion, entrega_apto,
            entrega_destinatario_nombre, entrega_destinatario_telefono,
            barrio:barrios!pedidos_entrega_barrio_id_fkey(nombre)
          `)
          .in('entrega_id', sinCliente)
        const byId = Object.fromEntries((peds || []).map(p => [p.entrega_id, p]))
        entregasArr.forEach(e => {
          if (e.cliente || !byId[e.id]) return
          const pe = byId[e.id]
          const destinatario = pe.entrega_destinatario_nombre
          e.cliente = {
            alias: destinatario ? `Para ${destinatario}` : null,
            barrio: pe.barrio?.nombre || '',
            direccion: pe.entrega_direccion || '',
            apto: pe.entrega_apto || '',
            tipo_vivienda: null,
            titular: { nombre: pe.comprador_nombre },
            beneficiario: destinatario ? { nombre: destinatario } : null,
          }
        })
      }
      setEntregas(entregasArr)
      setLoading(false)
    })()
  }, [params])

  // Renderiza QRs cuando todo está listo (uno por bulto)
  useEffect(() => {
    if (!qrReady || entregas.length === 0) return
    entregas.forEach(e => {
      const total = e.bultos_total || 1
      for (let i = 1; i <= total; i++) {
        const key = `${e.id}-${i}`
        const qrDiv = qrRefs.current[key]
        if (qrDiv && qrDiv.children.length === 0) {
          new window.QRCode(qrDiv, {
            text: `https://limpack.uy/d/${e.tracking_token}`,
            width: 160, height: 160,
            colorDark: '#000000', colorLight: '#FFFFFF',
            correctLevel: window.QRCode.CorrectLevel.M,
          })
        }
      }
    })
  }, [qrReady, entregas])

  const ventanaLabel = (v) => v === 'tarde' ? 'TARDE 14–18hs' : 'MAÑANA 10–14hs'
  const fechaLabel = (f) => {
    if (!f) return ''
    const d = new Date(f + 'T12:00:00')
    return d.toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'short' }).toUpperCase()
  }

  async function cambiarBultos(entregaId, nuevo) {
    const n = Math.max(1, Math.min(20, nuevo))
    // optimistic UI
    setEntregas(es => es.map(e => e.id === entregaId ? { ...e, bultos_total: n } : e))
    // limpia los refs viejos para que se vuelvan a renderizar
    Object.keys(qrRefs.current).forEach(k => { if (k.startsWith(entregaId + '-')) delete qrRefs.current[k] })
    try { await actualizarBultos(entregaId, n) } catch (err) { console.error('actualizarBultos:', err) }
  }

  if (loading) return <div className="loading">Cargando etiquetas...</div>

  if (entregas.length === 0) {
    return (
      <div className="page" style={{ padding: 32 }}>
        <h1>Sin etiquetas</h1>
        <p>No se encontraron entregas para imprimir. Volvé a /admin/entregas y seleccioná entregas.</p>
      </div>
    )
  }

  // Total de etiquetas = suma de bultos
  const totalLabels = entregas.reduce((s, e) => s + (e.bultos_total || 1), 0)

  return (
    <>
      <div className="etiquetas-toolbar no-print">
        <div>
          <strong>{totalLabels}</strong> etiquetas · {entregas.length} entrega{entregas.length !== 1 ? 's' : ''} · 100×150mm
        </div>
        <div>
          <button className="btn primary" onClick={() => window.print()}>🖨️ Imprimir</button>
          <button className="btn ghost" onClick={() => window.history.back()} style={{ marginLeft: 8 }}>← Volver</button>
        </div>
      </div>

      {/* Panel para configurar bultos por entrega — solo en pantalla */}
      <div className="bultos-config no-print">
        <div className="bc-title">Bultos por entrega</div>
        <div className="bc-list">
          {entregas.map(e => (
            <div key={e.id} className="bc-row">
              <div className="bc-info">
                <div className="bc-orden">{e.numero_orden}</div>
                <div className="bc-nombre">{(e.cliente?.beneficiario || e.cliente?.titular)?.nombre || '—'} · {e.cliente?.barrio}</div>
              </div>
              <div className="bc-controls">
                <button onClick={() => cambiarBultos(e.id, (e.bultos_total || 1) - 1)} disabled={(e.bultos_total || 1) <= 1}>−</button>
                <span className="bc-num">{e.bultos_total || 1}</span>
                <button onClick={() => cambiarBultos(e.id, (e.bultos_total || 1) + 1)}>+</button>
                <span className="bc-bulto">bulto{(e.bultos_total || 1) !== 1 ? 's' : ''}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {entregas.map(e => {
        const total = e.bultos_total || 1
        return Array.from({ length: total }, (_, i) => i + 1).map(idx => {
          const key = `${e.id}-${idx}`
          const tipoLabel = TIPO_LABEL[e.tipo] || e.tipo?.toUpperCase() || ''
          return (
            <div key={key} className="etiqueta">
              {/* HEADER: logo (texto) + tipo + bulto X/N */}
              <div className="et-head">
                <div className="et-brand-block">
                  <div className="et-logo-text">Lim<strong>pack</strong></div>
                  <div className="et-tipo">{tipoLabel}</div>
                </div>
                <div className="et-bulto">
                  <div className="et-bulto-num">{idx}<span className="et-bulto-de">/</span>{total}</div>
                  <div className="et-bulto-label">BULTO</div>
                </div>
              </div>

              {/* BANDA NEGRA: fecha + ventana */}
              <div className="et-fecha-band">
                <span>{fechaLabel(e.fecha_programada)}</span>
                <span className="et-fecha-sep">·</span>
                <span>{ventanaLabel(e.ventana_horaria)}</span>
              </div>

              {/* NÚMERO DE PAQUETE — compacto, B&N para térmica */}
              <div style={{
                textAlign: 'center', margin: '2mm 0',
                padding: '1.5mm 0', borderRadius: 4,
                background: '#fff', border: '2px solid #000',
              }}>
                <div style={{ fontSize: 7, color: '#000', fontWeight: 800, letterSpacing: '.12em' }}>PAQUETE</div>
                <div style={{ fontSize: 18, color: '#000', fontWeight: 900, letterSpacing: '-.02em', lineHeight: 1 }}>
                  #{(e.numero_orden || '').split('-').pop() || '?'}
                </div>
              </div>

              {/* DESTINATARIO */}
              <div className="et-dest-label">DESTINATARIO</div>
              <div className="et-nombre">{(e.cliente?.beneficiario || e.cliente?.titular)?.nombre || '—'}</div>

              {/* DIRECCIÓN — caja con borde grueso */}
              <div className="et-direccion">
                <div className="et-barrio-row">
                  <span className="et-barrio">{e.cliente?.barrio}</span>
                  {e.cliente?.tipo_vivienda && (
                    <span className="et-vivienda">{VIVIENDA_LABEL[e.cliente.tipo_vivienda]}</span>
                  )}
                </div>
                <div className="et-calle">{e.cliente?.direccion}{e.cliente?.apto ? ` · ${e.cliente.apto}` : ''}</div>
              </div>

              {/* REFERIDO (si vino por portero) */}
              {e.cliente?.portero && (
                <div className="et-referido">
                  <span className="et-referido-tag">REFERIDO</span>
                  <span className="et-referido-txt">
                    {e.cliente.portero.profile?.nombre}
                    {e.cliente.portero.lugar ? ` · ${e.cliente.portero.lugar}` : ''}
                  </span>
                </div>
              )}

              {/* NOTAS */}
              {e.notas && (
                <div className="et-notas">
                  <div className="et-notas-label">★ NOTAS</div>
                  <div className="et-notas-txt">{e.notas}</div>
                </div>
              )}

              {/* QR + CONTENIDO en 2 columnas */}
              <div className="et-bottom">
                {/* QR a la izquierda */}
                <div className="et-qr-block">
                  <div className="et-qr" ref={el => { qrRefs.current[key] = el }}></div>
                  <div className="et-qr-cap">
                    <div className="et-qr-cap-1">ESCANEAR</div>
                    <div className="et-qr-cap-2">limpack.uy/d/{(e.tracking_token || '').slice(0, 8)}…</div>
                  </div>
                </div>

                {/* Contenido del kit + extras a la derecha */}
                <div className="et-kit">
                  <div className="et-kit-label">CONTENIDO</div>
                  <div className="et-kit-nombre">{e.kit?.nombre || '— sin kit —'}</div>
                  {(e.productos_extras || []).length > 0 && (
                    <div className="et-extras">
                      <div className="et-extras-label">+ EXTRAS</div>
                      <ul className="et-extras-list">
                        {(e.productos_extras || []).map((x, i) => {
                          const cantidad = x?.cantidad ?? x?.qty ?? 1
                          const nombre = typeof x === 'string' ? x : (x?.nombre || x?.sku || JSON.stringify(x))
                          return <li key={i}>{cantidad} × {nombre}</li>
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer compacto */}
              <div className="et-foot">
                <span className="et-orden-num">{e.numero_orden}</span>
                <span className="et-web">limpack.uy</span>
              </div>
            </div>
          )
        })
      })}

      <style>{`
        @page { size: 100mm 150mm; margin: 0; }
        @media print {
          body { background: #fff; margin: 0; }
          .no-print { display: none !important; }
          .etiqueta { page-break-after: always; box-shadow: none; margin: 0; }
        }
        @media screen {
          body { background: #E5E7EB; }
        }

        .etiquetas-toolbar {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          background: var(--bg-dark); color: #fff;
          padding: 12px 24px; display: flex; align-items: center; justify-content: space-between;
        }

        .bultos-config {
          max-width: 100mm; margin: 76px auto 12mm; background: #fff;
          border-radius: 10px; padding: 16px; box-shadow: 0 4px 14px rgba(0,0,0,.08);
        }
        .bc-title {
          font-size: 11px; font-weight: 800; letter-spacing: .12em;
          text-transform: uppercase; color: #555; margin-bottom: 10px;
          padding-bottom: 8px; border-bottom: 1px solid #eee;
        }
        .bc-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 8px 0; border-bottom: 1px dashed #eee;
        }
        .bc-row:last-child { border-bottom: none }
        .bc-info { flex: 1; min-width: 0 }
        .bc-orden { font-family: monospace; font-size: 11px; color: #888 }
        .bc-nombre { font-size: 13px; font-weight: 600; color: #111 }
        .bc-controls { display: flex; align-items: center; gap: 6px }
        .bc-controls button {
          width: 28px; height: 28px; border-radius: 6px;
          background: #111; color: #fff; border: 0; font-size: 16px;
          cursor: pointer; line-height: 1;
        }
        .bc-controls button:disabled { background: #ccc; cursor: not-allowed }
        .bc-num {
          min-width: 22px; text-align: center; font-weight: 800; font-size: 16px;
        }
        .bc-bulto { font-size: 11px; color: #666; margin-left: 4px }

        /* ============ ETIQUETA (B&N) ============ */
        .etiqueta {
          width: 100mm; height: 150mm;
          background: #fff; color: #000;
          margin: 0 auto 12mm;
          padding: 5mm 6mm;  /* Mas margen lateral para que entre todo en impresion termica */
          font-family: 'Inter', sans-serif;
          box-shadow: 0 4px 16px rgba(0,0,0,.15);
          display: flex; flex-direction: column;
          page-break-after: always;
          page-break-inside: avoid;
          break-inside: avoid;
          overflow: hidden;
          border: 1px solid #000;
          box-sizing: border-box;
        }

        /* HEADER */
        .et-head {
          display: flex; justify-content: space-between; align-items: stretch;
          gap: 3mm; margin-bottom: 2mm;
        }
        .et-brand-block {
          flex: 1; min-width: 0;
          display: flex; flex-direction: column; gap: 1mm; align-items: flex-start;
        }
        .et-logo-text {
          font-size: 18px; font-weight: 400;
          color: #000; letter-spacing: -.02em; line-height: 1;
        }
        .et-logo-text strong { font-weight: 900; }
        .et-tipo {
          display: inline-block;
          font-size: 9px; font-weight: 800; letter-spacing: .15em;
          padding: 1.2mm 2.5mm; border: 1.5px solid #000; border-radius: 2px;
        }
        .et-bulto {
          min-width: 24mm;
          border: 2.5px solid #000; border-radius: 4px;
          padding: 1.5mm 2mm; text-align: center;
          background: #000; color: #fff;
          display: flex; flex-direction: column; justify-content: center;
        }
        .et-bulto-num {
          font-size: 24px; font-weight: 900; line-height: 1;
          letter-spacing: -.04em;
        }
        .et-bulto-de { font-weight: 400; opacity: .7; margin: 0 1px }
        .et-bulto-label {
          font-size: 7px; font-weight: 700; letter-spacing: .2em; margin-top: 0.5mm;
        }

        /* BANDA NEGRA — fecha */
        .et-fecha-band {
          background: #000; color: #fff;
          padding: 1.5mm 3mm; margin-bottom: 2mm;
          font-size: 10px; font-weight: 800; letter-spacing: .08em;
          text-align: center;
        }
        .et-fecha-sep { margin: 0 2mm; opacity: .6 }

        /* DESTINATARIO */
        .et-dest-label {
          font-size: 7px; font-weight: 700; letter-spacing: .2em;
          color: #555; margin-bottom: 0.3mm;
        }
        .et-nombre {
          font-size: 16px; font-weight: 900; letter-spacing: -.02em;
          line-height: 1.05; text-transform: uppercase; margin-bottom: 2mm;
        }

        /* DIRECCIÓN — caja con borde negro grueso */
        .et-direccion {
          border: 1.5px solid #000; border-radius: 3px;
          padding: 2mm 3mm; margin-bottom: 2mm;
        }
        .et-barrio-row {
          display: flex; justify-content: space-between; align-items: center;
          padding-bottom: 1mm; border-bottom: 1px solid #000; margin-bottom: 1mm;
          gap: 2mm;
        }
        .et-barrio {
          font-size: 10px; font-weight: 800; letter-spacing: .15em;
          text-transform: uppercase;
        }
        .et-vivienda {
          font-size: 10px; font-weight: 800; letter-spacing: .05em;
          padding: 0.5mm 2mm; background: #000; color: #fff;
          border-radius: 2px; white-space: nowrap;
        }
        .et-calle { font-size: 14px; font-weight: 700; line-height: 1.2 }

        /* REFERIDO */
        .et-referido {
          display: flex; align-items: center; gap: 2mm;
          margin-bottom: 3mm; padding: 1.5mm 2.5mm;
          background: #000; color: #fff; border-radius: 2px;
        }
        .et-referido-tag {
          font-size: 8px; font-weight: 800; letter-spacing: .2em;
          padding: 0.5mm 1.5mm; border: 1px solid #fff; border-radius: 2px;
          flex-shrink: 0;
        }
        .et-referido-txt {
          font-size: 10px; font-weight: 700; line-height: 1.2;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }

        /* NOTAS */
        .et-notas {
          border: 1.5px dashed #000; padding: 2mm 3mm;
          margin-bottom: 3mm; border-radius: 2px;
        }
        .et-notas-label {
          font-size: 8px; font-weight: 800; letter-spacing: .2em; margin-bottom: 0.8mm;
        }
        .et-notas-txt { font-size: 11px; font-weight: 600; line-height: 1.3 }

        /* BOTTOM — QR izquierda + Contenido derecha */
        .et-bottom {
          margin-top: 2mm;
          display: flex; gap: 3mm;
          padding-top: 2mm; border-top: 2px solid #000;
          align-items: stretch;
          flex: 1;
        }

        /* CONTENIDO (columna derecha) */
        .et-kit {
          flex: 1; min-width: 0;
          display: flex; flex-direction: column;
        }
        .et-kit-label {
          font-size: 7px; font-weight: 700; letter-spacing: .2em; color: #555; margin-bottom: 0.8mm;
        }
        .et-kit-nombre {
          font-size: 18px; font-weight: 900; line-height: 1.1;
          letter-spacing: -.01em;
        }
        .et-extras {
          margin-top: 1.5mm; padding: 1mm 2mm;
          border: 1px solid #000; border-radius: 2px;
          flex: 1; overflow: hidden;
        }
        .et-extras-label {
          font-size: 7px; font-weight: 800; letter-spacing: .2em;
          margin-bottom: 0.8mm;
        }
        .et-extras-list {
          margin: 0; padding-left: 3mm;
          font-size: 9px; font-weight: 600; line-height: 1.3;
        }
        .et-extras-list li { margin: 0 }

        /* QR (columna izquierda) */
        .et-qr-block {
          display: flex; flex-direction: column; align-items: center;
          padding: 1.5mm; border: 2px solid #000; border-radius: 4px;
          background: #fff;
          flex-shrink: 0;
        }
        .et-qr {
          width: 38mm; height: 38mm;
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
        }
        .et-qr img { display: block; width: 100% !important; height: 100% !important; }
        .et-qr canvas { display: none !important; }
        .et-qr-cap { text-align: center; margin-top: 1mm; width: 100% }
        .et-qr-cap-1 {
          font-size: 7px; font-weight: 800; letter-spacing: .12em;
        }
        .et-qr-cap-2 {
          font-size: 6px; font-family: monospace; color: #555; margin-top: 0.3mm;
          word-break: break-all;
        }

        /* FOOTER */
        .et-foot {
          margin-top: 2mm; padding-top: 1.5mm;
          border-top: 1px dashed #000;
          display: flex; justify-content: space-between;
          font-size: 9px; font-family: monospace; font-weight: 700;
          flex-shrink: 0;
        }
        .et-orden-num { font-weight: 800; letter-spacing: .05em }
        .et-web { color: #555 }

        body { padding-top: 56px; }
      `}</style>
    </>
  )
}
